import { useEffect, useState, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "../components/ui/alert-dialog";
import {
  Users, Loader2, Copy, Check, Plus, LogIn, Crown, ShieldCheck, Lock,
  CheckCircle2, ArrowRight, ArrowLeft, EyeOff, MapPin, Share2,
  MessageSquare, MessageCircle, Mail, Trash2, LogOut,
} from "lucide-react";

const inviteText = (pod) =>
  `Join my Errandell pod "${pod.name}" — a private, trusted circle for local errands. Tap to join: ${window.location.origin}/pods?code=${pod.invite_code}`;

function ShareInvite({ pod, testidPrefix = "share" }) {
  const text = inviteText(pod);
  const enc = encodeURIComponent(text);
  const native = async () => {
    if (navigator.share) {
      try { await navigator.share({ title: "Join my Errandell pod", text }); } catch (e) { if (e?.name !== "AbortError") console.error("Share failed", e); }
    } else {
      navigator.clipboard.writeText(text);
      toast.success("Invite message copied — paste it to a neighbor!");
    }
  };
  const links = [
    { key: "whatsapp", icon: MessageCircle, label: "WhatsApp", href: `https://wa.me/?text=${enc}`, cls: "text-[#25D366]" },
    { key: "sms", icon: MessageSquare, label: "Text", href: `sms:?&body=${enc}`, cls: "text-[#2F8F6E]" },
    { key: "email", icon: Mail, label: "Email", href: `mailto:?subject=${encodeURIComponent("Join my Errandell pod")}&body=${enc}`, cls: "text-primary" },
  ];
  return (
    <div className="flex flex-wrap items-center justify-center gap-2" data-testid={`${testidPrefix}-invite-row`}>
      <button data-testid={`${testidPrefix}-native-btn`} onClick={native}
        className="inline-flex items-center gap-1.5 rounded-full bg-primary text-white px-4 py-2 text-sm font-bold hover:bg-[#FF8D70] transition-transform hover:scale-105 active:scale-95">
        <Share2 size={15} /> Invite a neighbor
      </button>
      {links.map((l) => (
        <a key={l.key} data-testid={`${testidPrefix}-${l.key}-btn`} href={l.href} target="_blank" rel="noreferrer"
          className={`inline-flex items-center gap-1.5 rounded-full bg-muted px-3.5 py-2 text-sm font-semibold text-[#4A5361] hover:bg-gray-200 transition-colors`}>
          <l.icon size={15} className={l.cls} /> {l.label}
        </a>
      ))}
    </div>
  );
}

const container = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.35, staggerChildren: 0.08 } },
  exit: { opacity: 0, x: -24, transition: { duration: 0.2 } },
};
const item = {
  hidden: { opacity: 0, y: 10 },
  visible: { opacity: 1, y: 0 },
};

const STEPS = ["Benefits", "Plan", "Your pod", "Done"];

function StepIndicator({ current }) {
  const pct = ((current - 1) / (STEPS.length - 1)) * 100;
  return (
    <div className="mb-10" data-testid="pods-step-indicator">
      <div className="relative flex items-center justify-between">
        <div className="absolute left-0 right-0 top-1/2 -translate-y-1/2 h-1 bg-gray-100 rounded-full" />
        <div
          className="absolute left-0 top-1/2 -translate-y-1/2 h-1 bg-primary rounded-full transition-all duration-500"
          style={{ width: `${pct}%` }}
        />
        {STEPS.map((label, i) => {
          const n = i + 1;
          const done = n < current;
          const activeNode = n === current;
          return (
            <div key={label} className="relative z-10 flex flex-col items-center gap-2"
              aria-current={activeNode ? "step" : undefined}>
              <div className={`w-9 h-9 rounded-full flex items-center justify-center text-sm font-bold border-2 transition-colors ${
                done ? "bg-[#2F8F6E] border-[#2F8F6E] text-white"
                : activeNode ? "bg-primary border-primary text-white"
                : "bg-white border-gray-200 text-[#9CA3AF]"}`}>
                {done ? <Check size={16} /> : n}
              </div>
              <span className={`text-[11px] font-semibold hidden sm:block ${activeNode ? "text-[#1A1D20]" : "text-[#9CA3AF]"}`}>{label}</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const BENEFITS = [
  { icon: EyeOff, title: "Invite-only privacy", body: "Only vetted neighbors in your pod can see and accept your errands — never strangers." },
  { icon: ShieldCheck, title: "A trusted circle", body: "Build a small, safe community of people you actually know and recognize." },
  { icon: Users, title: "Faster help", body: "Post once and the right neighbors get it instantly — no public feed noise." },
];

export default function Pods() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [membership, setMembership] = useState(null);
  const [pods, setPods] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [podName, setPodName] = useState("");
  const [code, setCode] = useState("");
  const [copied, setCopied] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const { user } = useAuth();

  const leavePod = async (p) => {
    try {
      await api.post(`/pods/${p.id}/leave`);
      toast.success(`Left "${p.name}"`);
      await load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const deletePod = async (p) => {
    if (!p) return;
    try {
      await api.delete(`/pods/${p.id}`);
      toast.success(`Deleted "${p.name}"`);
      setDeleteTarget(null);
      await load();
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
  };
  const [introStep, setIntroStep] = useState(1); // 1 = benefits, 2 = pricing
  const [successPod, setSuccessPod] = useState(null);
  const [invitedCode, setInvitedCode] = useState("");
  const inviteHandled = useRef(false);

  const load = async () => {
    setLoading(true);
    try {
      const [m, p] = await Promise.all([api.get("/pods/membership"), api.get("/pods/mine")]);
      setMembership(m.data);
      setPods(p.data);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  // Capture an incoming invite code (from ?code= or a stashed value that survived login/payment).
  useEffect(() => {
    const pending = (params.get("code") || sessionStorage.getItem("pendingPodCode") || "").toUpperCase();
    if (pending) {
      sessionStorage.setItem("pendingPodCode", pending);
      setInvitedCode(pending);
      setCode(pending);
    }
  }, [params]);

  const doJoin = async (codeValue) => {
    const { data } = await api.post("/pods/join", { invite_code: codeValue });
    await load();
    return data;
  };

  // One-click join: as soon as an active member lands here with a pending invite, join automatically.
  useEffect(() => {
    if (loading || inviteHandled.current) return;
    const pending = sessionStorage.getItem("pendingPodCode");
    if (!pending || !membership?.active) return;
    inviteHandled.current = true;
    (async () => {
      try {
        const pod = await doJoin(pending);
        sessionStorage.removeItem("pendingPodCode");
        setInvitedCode("");
        setCode("");
        setSuccessPod(pod);
        toast.success(`You joined ${pod.name}!`);
      } catch (err) {
        toast.error(apiError(err.response?.data?.detail) || "That invite code is invalid.");
        sessionStorage.removeItem("pendingPodCode");
        setInvitedCode("");
      }
    })();
  }, [loading, membership]); // eslint-disable-line react-hooks/exhaustive-deps

  const subscribe = async (plan) => {
    setBusy(true);
    try {
      const { data } = await api.post("/pods/subscribe", { plan, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); setBusy(false); }
  };

  const createPod = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/pods", { name: podName });
      setPodName("");
      toast.success("Pod created!");
      await load();
      setSuccessPod(data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const joinPod = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const data = await doJoin(code);
      setCode("");
      setInvitedCode("");
      sessionStorage.removeItem("pendingPodCode");
      toast.success("Joined the pod!");
      setSuccessPod(data);
    } catch (err) { toast.error(apiError(err.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const copy = (c) => { navigator.clipboard.writeText(c); setCopied(c); setTimeout(() => setCopied(""), 1500); toast.success("Invite code copied!"); };

  if (loading) return <div className="flex justify-center py-20 text-[#6B7280]"><Loader2 className="animate-spin" /></div>;

  const active = membership?.active;

  // ---- Determine which view/step to render ----
  let currentStep;
  if (successPod) currentStep = 4;
  else if (!active) currentStep = introStep; // 1 or 2
  else if (pods.length === 0) currentStep = 3;
  else currentStep = null; // member dashboard

  const Header = () => (
    <div className="mb-6">
      <div className="flex items-center gap-2 mb-1">
        <Users className="text-primary" size={26} />
        <h1 className="font-heading font-black text-3xl sm:text-4xl tracking-tight text-[#1A1D20]">Neighbor Pods</h1>
      </div>
      <p className="text-[#6B7280]">Private cohorts where you post and accept errands only among trusted members.</p>
    </div>
  );

  return (
    <div className="max-w-4xl mx-auto px-5 py-8 sm:py-12">
      <Header />

      {invitedCode && !active && (
        <div data-testid="pods-invite-banner" className="bg-[#FFF3EE] border border-[#FFD9CB] rounded-2xl p-4 mb-6 flex items-start gap-2.5 text-sm text-[#B4552F]">
          <Users size={17} className="mt-0.5 shrink-0" />
          <span>You've been invited to join pod <strong className="font-heading tracking-wide">{invitedCode}</strong>. Become a member below and you'll be added automatically.</span>
        </div>
      )}

      {currentStep === null ? (
        // ---------------- MEMBER DASHBOARD ----------------
        <motion.div variants={container} initial="hidden" animate="visible" data-testid="member-dashboard">
          <div className="bg-[#E7F6F0] border border-[#CDEBDF] rounded-2xl p-4 mb-6 flex items-center gap-2 text-sm font-semibold text-[#2F8F6E]">
            <Users size={16} /> Pods are free for verified neighbors — create or join as many as you like.
          </div>

          <div className="grid sm:grid-cols-2 gap-4 mb-8">
            <form onSubmit={createPod} className="bg-white rounded-3xl border border-gray-100 shadow-soft p-6">
              <p className="font-heading font-extrabold text-[#1A1D20] mb-3 flex items-center gap-1.5"><Plus size={16} /> Create a pod</p>
              <Input data-testid="pod-name-input" value={podName} onChange={(e) => setPodName(e.target.value)} required placeholder="Valmonte Parents"
                className="rounded-2xl h-11 border-gray-200 mb-3" />
              <Button data-testid="create-pod-btn" type="submit" disabled={busy} className="w-full rounded-full font-bold bg-primary hover:bg-[#FF8D70]">Create</Button>
            </form>
            <form onSubmit={joinPod} className={`bg-white rounded-3xl border shadow-soft p-6 transition-colors ${invitedCode ? "border-2 border-primary" : "border-gray-100"}`}>
              <p className="font-heading font-extrabold text-[#1A1D20] mb-3 flex items-center gap-1.5"><LogIn size={16} /> Join with code</p>
              <Input data-testid="pod-code-input" value={code} onChange={(e) => setCode(e.target.value)} required placeholder="e.g. A1B2C3"
                className={`rounded-2xl h-11 mb-3 uppercase ${invitedCode ? "border-primary ring-2 ring-primary/20" : "border-gray-200"}`} />
              <Button data-testid="join-pod-btn" type="submit" disabled={busy} className="w-full rounded-full font-bold bg-secondary hover:opacity-90">Join</Button>
            </form>
          </div>

          <h2 className="font-heading font-extrabold text-xl text-[#1A1D20] mb-3">Your pods ({pods.length})</h2>
          <div className="space-y-3">
            {pods.map((p) => {
              const isOwner = p.owner_id === user?.id;
              return (
              <div key={p.id} data-testid={`pod-${p.id}`} className="bg-white rounded-2xl border border-gray-100 shadow-soft p-4">
                <div className="flex items-center justify-between gap-2">
                  <button data-testid={`pod-open-${p.id}`} onClick={() => navigate(`/pods/${p.id}`)} className="min-w-0 flex-1 text-left group">
                    <p className="font-bold text-[#1A1D20] group-hover:text-primary transition-colors flex items-center gap-1.5">
                      {p.name} {isOwner && <Crown size={14} className="text-[#FFB020]" />}
                    </p>
                    <p className="text-xs text-[#6B7280] flex items-center gap-1">{p.member_count} member{p.member_count === 1 ? "" : "s"} · View details <ArrowRight size={11} /></p>
                  </button>
                  <button data-testid={`pod-copy-${p.id}`} onClick={() => copy(p.invite_code)}
                    className="flex items-center gap-1.5 bg-muted rounded-full px-3 py-1.5 text-sm font-bold text-[#4A5361] hover:bg-gray-200 transition-colors shrink-0">
                    {copied === p.invite_code ? <Check size={14} className="text-[#2F8F6E]" /> : <Copy size={14} />} {p.invite_code}
                  </button>
                </div>
                <div className="mt-3 pt-3 border-t border-gray-100 flex items-center justify-between gap-2">
                  <ShareInvite pod={p} testidPrefix={`pod-share-${p.id}`} />
                  {isOwner ? (
                    <button data-testid={`pod-delete-${p.id}`} onClick={() => setDeleteTarget(p)}
                      className="flex items-center gap-1.5 text-sm font-bold text-[#B23B3B] hover:bg-[#FBE9E9] rounded-full px-3 py-1.5 transition-colors">
                      <Trash2 size={14} /> Delete
                    </button>
                  ) : (
                    <button data-testid={`pod-leave-${p.id}`} onClick={() => leavePod(p)}
                      className="flex items-center gap-1.5 text-sm font-bold text-[#6B7280] hover:bg-gray-100 rounded-full px-3 py-1.5 transition-colors">
                      <LogOut size={14} /> Leave
                    </button>
                  )}
                </div>
              </div>
              );
            })}
          </div>

          <AlertDialog open={!!deleteTarget} onOpenChange={(o) => !o && setDeleteTarget(null)}>
            <AlertDialogContent className="rounded-3xl" data-testid="pod-delete-dialog">
              <AlertDialogHeader>
                <AlertDialogTitle className="font-heading">Delete "{deleteTarget?.name}"?</AlertDialogTitle>
                <AlertDialogDescription>
                  This permanently removes the pod for all {deleteTarget?.member_count} member{deleteTarget?.member_count === 1 ? "" : "s"}. Errands posted to it become public. This can't be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel className="rounded-full font-semibold">Keep pod</AlertDialogCancel>
                <AlertDialogAction data-testid="pod-delete-confirm" onClick={(ev) => { ev.preventDefault(); deletePod(deleteTarget); }}
                  className="rounded-full font-bold bg-[#B23B3B] hover:bg-[#9c3333]">Delete pod</AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </motion.div>
      ) : (
        // ---------------- WIZARD ----------------
        <>
          <StepIndicator current={currentStep} />
          <div className="bg-white rounded-3xl shadow-soft border border-gray-100 p-6 sm:p-10 overflow-hidden relative">
            <AnimatePresence mode="wait">

              {currentStep === 1 && (
                <motion.div key="s1" variants={container} initial="hidden" animate="visible" exit="exit"
                  data-testid="step-intro" className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
                  <div>
                    <motion.span variants={item} className="inline-flex items-center gap-1.5 bg-[#E7F6F0] text-[#2F8F6E] text-xs font-bold px-3 py-1.5 rounded-full mb-4">
                      <Crown size={13} /> Members-only
                    </motion.span>
                    <motion.h2 variants={item} className="font-heading font-black text-3xl sm:text-4xl tracking-tight text-[#1A1D20] leading-tight mb-3">
                      Your trusted circle for local errands.
                    </motion.h2>
                    <motion.p variants={item} className="text-[#6B7280] text-base sm:text-lg mb-6">
                      Pods are private, invite-only groups. Post and accept errands only among neighbors you trust — safer and quieter than the public feed.
                    </motion.p>
                    <motion.div variants={item}>
                      <Button data-testid="intro-get-started-btn" onClick={() => setIntroStep(2)}
                        className="rounded-full h-12 px-7 font-bold bg-primary hover:bg-[#FF8D70] transition-transform hover:scale-105 active:scale-95">
                        Get started <ArrowRight size={17} className="ml-1.5" />
                      </Button>
                    </motion.div>
                  </div>
                  <motion.div variants={item} className="bg-[#E7F6F0] rounded-3xl p-6 sm:p-8 space-y-5">
                    {BENEFITS.map((b) => (
                      <div key={b.title} className="flex gap-3.5">
                        <div className="shrink-0 w-10 h-10 rounded-2xl bg-white flex items-center justify-center text-[#2F8F6E]">
                          <b.icon size={19} />
                        </div>
                        <div>
                          <p className="font-bold text-[#1A1D20] text-sm">{b.title}</p>
                          <p className="text-sm text-[#5B6470] leading-snug">{b.body}</p>
                        </div>
                      </div>
                    ))}
                  </motion.div>
                </motion.div>
              )}

              {currentStep === 2 && (
                <motion.div key="s2" variants={container} initial="hidden" animate="visible" exit="exit" data-testid="step-pricing">
                  <motion.div variants={item} className="text-center mb-8">
                    <h2 className="font-heading font-black text-3xl tracking-tight text-[#1A1D20] mb-1">Choose your membership</h2>
                    <p className="text-[#6B7280]">Unlock creating and joining pods. Cancel anytime.</p>
                  </motion.div>
                  <div className="grid sm:grid-cols-2 gap-4 sm:gap-6 max-w-2xl mx-auto">
                    <motion.div variants={item} className="rounded-3xl border-2 border-gray-200 p-7 flex flex-col">
                      <p className="text-sm font-bold text-[#6B7280] mb-1">Monthly</p>
                      <p className="font-heading font-black text-4xl text-[#1A1D20]">$3<span className="text-base font-bold text-[#9CA3AF]">/mo</span></p>
                      <p className="text-sm text-[#6B7280] mt-1 mb-5">Billed monthly</p>
                      <ul className="text-sm text-[#5B6470] space-y-2 mb-6 flex-1">
                        <li className="flex gap-2"><Check size={16} className="text-[#2F8F6E] shrink-0" /> Create unlimited pods</li>
                        <li className="flex gap-2"><Check size={16} className="text-[#2F8F6E] shrink-0" /> Join with invite codes</li>
                      </ul>
                      <Button data-testid="subscribe-monthly-btn" onClick={() => subscribe("monthly")} disabled={busy}
                        variant="outline" className="w-full rounded-full h-11 font-bold border-2 border-gray-200 hover:border-primary hover:text-primary transition-colors">
                        {busy ? <Loader2 size={16} className="animate-spin" /> : "Choose monthly"}
                      </Button>
                    </motion.div>
                    <motion.div variants={item} className="rounded-3xl border-2 border-primary bg-[#E7F6F0] p-7 relative flex flex-col shadow-lg sm:-translate-y-2">
                      <span className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white text-[10px] font-bold px-3 py-1 rounded-full whitespace-nowrap">BEST VALUE · SAVE 58%</span>
                      <p className="text-sm font-bold text-[#2F8F6E] mb-1">Yearly</p>
                      <p className="font-heading font-black text-4xl text-[#1A1D20]">$15<span className="text-base font-bold text-[#6B9B8A]">/yr</span></p>
                      <p className="text-sm text-[#5B6470] mt-1 mb-5">Just $1.25/mo</p>
                      <ul className="text-sm text-[#3F5A50] space-y-2 mb-6 flex-1">
                        <li className="flex gap-2"><Check size={16} className="text-[#2F8F6E] shrink-0" /> Everything in monthly</li>
                        <li className="flex gap-2"><Check size={16} className="text-[#2F8F6E] shrink-0" /> Best price, one payment</li>
                      </ul>
                      <Button data-testid="subscribe-yearly-btn" onClick={() => subscribe("yearly")} disabled={busy}
                        className="w-full rounded-full h-11 font-bold bg-primary hover:bg-[#FF8D70] transition-transform hover:scale-105 active:scale-95">
                        {busy ? <Loader2 size={16} className="animate-spin" /> : "Choose yearly"}
                      </Button>
                    </motion.div>
                  </div>
                  <motion.div variants={item} className="flex items-center justify-center gap-1.5 text-xs text-[#9CA3AF] mt-6">
                    <Lock size={13} /> Secure checkout powered by Stripe
                  </motion.div>
                  <motion.div variants={item} className="text-center mt-4">
                    <button data-testid="pricing-back-btn" onClick={() => setIntroStep(1)}
                      className="inline-flex items-center gap-1 text-sm font-semibold text-[#6B7280] hover:text-[#1A1D20] transition-colors">
                      <ArrowLeft size={15} /> Back
                    </button>
                  </motion.div>
                </motion.div>
              )}

              {currentStep === 3 && (
                <motion.div key="s3" variants={container} initial="hidden" animate="visible" exit="exit" data-testid="step-create-join">
                  <motion.div variants={item} className="text-center mb-8">
                    <span className="inline-flex items-center gap-1.5 bg-[#E7F6F0] text-[#2F8F6E] text-xs font-bold px-3 py-1.5 rounded-full mb-3">
                      <CheckCircle2 size={13} /> Free for neighbors
                    </span>
                    <h2 className="font-heading font-black text-3xl tracking-tight text-[#1A1D20] mb-1">Set up your first pod</h2>
                    <p className="text-[#6B7280]">Start a new pod or join an existing one with an invite code.</p>
                  </motion.div>
                  <div className="grid md:grid-cols-2 gap-6 md:gap-8 md:divide-x divide-gray-100">
                    <motion.form variants={item} onSubmit={createPod} className="md:pr-8">
                      <p className="font-heading font-extrabold text-[#1A1D20] mb-1 flex items-center gap-1.5"><Plus size={17} /> Create a pod</p>
                      <p className="text-sm text-[#6B7280] mb-4">You'll get a shareable invite code.</p>
                      <Label htmlFor="pod-name" className="font-semibold text-sm">Pod name</Label>
                      <Input id="pod-name" data-testid="pod-name-input" value={podName} onChange={(e) => setPodName(e.target.value)} required placeholder="Valmonte Parents"
                        className="mt-1.5 rounded-2xl h-12 border-gray-200 mb-4" />
                      <Button data-testid="create-pod-btn" type="submit" disabled={busy}
                        className="w-full rounded-full h-11 font-bold bg-primary hover:bg-[#FF8D70]">
                        {busy ? <Loader2 size={16} className="animate-spin" /> : "Create pod"}
                      </Button>
                    </motion.form>
                    <motion.form variants={item} onSubmit={joinPod} className="md:pl-8 pt-6 md:pt-0 border-t md:border-t-0 border-gray-100">
                      <p className="font-heading font-extrabold text-[#1A1D20] mb-1 flex items-center gap-1.5"><LogIn size={17} /> Join a pod</p>
                      <p className="text-sm text-[#6B7280] mb-4">Got a code from a neighbor?</p>
                      <Label htmlFor="pod-code" className="font-semibold text-sm">Invite code</Label>
                      <Input id="pod-code" data-testid="pod-code-input" value={code} onChange={(e) => setCode(e.target.value)} required placeholder="e.g. A1B2C3"
                        className={`mt-1.5 rounded-2xl h-12 mb-4 uppercase ${invitedCode ? "border-primary ring-2 ring-primary/20" : "border-gray-200"}`} />
                      <Button data-testid="join-pod-btn" type="submit" disabled={busy}
                        className="w-full rounded-full h-11 font-bold bg-secondary hover:opacity-90">
                        {busy ? <Loader2 size={16} className="animate-spin" /> : "Join pod"}
                      </Button>
                    </motion.form>
                  </div>
                </motion.div>
              )}

              {currentStep === 4 && (
                <motion.div key="s4" variants={container} initial="hidden" animate="visible" exit="exit"
                  data-testid="step-success" className="flex flex-col items-center text-center py-6">
                  <motion.div variants={item} initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: "spring", stiffness: 260, damping: 16 }}
                    className="w-20 h-20 rounded-full bg-[#E7F6F0] flex items-center justify-center text-[#2F8F6E] mb-5">
                    <CheckCircle2 size={44} />
                  </motion.div>
                  <motion.h2 variants={item} className="font-heading font-black text-3xl tracking-tight text-[#1A1D20] mb-1">You're all set!</motion.h2>
                  <motion.p variants={item} className="text-[#6B7280] mb-6">
                    <span className="font-bold text-[#1A1D20]">{successPod.name}</span> is ready. Share this code to invite neighbors.
                  </motion.p>
                  <motion.button variants={item} data-testid="success-copy-code-btn" onClick={() => copy(successPod.invite_code)}
                    className="flex items-center gap-2 bg-muted rounded-2xl px-5 py-3 font-heading font-black text-2xl tracking-widest text-[#1A1D20] hover:bg-gray-200 transition-colors mb-5">
                    {successPod.invite_code}
                    {copied === successPod.invite_code ? <Check size={20} className="text-[#2F8F6E]" /> : <Copy size={18} className="text-[#6B7280]" />}
                  </motion.button>
                  <motion.p variants={item} className="text-xs font-semibold text-[#9CA3AF] mb-3">One tap to invite — pods only grow when you bring neighbors in.</motion.p>
                  <motion.div variants={item} className="mb-8">
                    <ShareInvite pod={successPod} testidPrefix="success-share" />
                  </motion.div>
                  <motion.div variants={item} className="flex flex-col sm:flex-row gap-3 w-full sm:w-auto">
                    <Button data-testid="success-post-errand-btn" onClick={() => navigate("/post")}
                      className="rounded-full h-12 px-6 font-bold bg-primary hover:bg-[#FF8D70] transition-transform hover:scale-105 active:scale-95">
                      <MapPin size={16} className="mr-1.5" /> Post your first pod errand
                    </Button>
                    <Button data-testid="success-view-pods-btn" onClick={() => setSuccessPod(null)}
                      variant="outline" className="rounded-full h-12 px-6 font-bold border-2 border-gray-200">
                      View my pods
                    </Button>
                  </motion.div>
                </motion.div>
              )}

            </AnimatePresence>
          </div>
        </>
      )}
    </div>
  );
}
